"use client";

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { Button } from "@/components/ui/button";
import { cf } from "@/components/brand/sfturnos-styles";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

type PlanKey = "FREE" | "PRO" | "ENTERPRISE";

const planCards: { key: PlanKey; title: string; blurb: string; accent: string }[] = [
  {
    key: "FREE",
    title: "Free",
    blurb: "100 turnos/mes, 1 agenda, email. Sin WhatsApp ni cobros online.",
    accent: "#64748b",
  },
  {
    key: "PRO",
    title: "Pro",
    blurb: "Turnos ilimitados, hasta 3 agendas, WhatsApp y Mercado Pago.",
    accent: "token(colors.brand.600)",
  },
  {
    key: "ENTERPRISE",
    title: "Enterprise",
    blurb: "Agendas extra y equipos grandes. Incluye todo lo del plan Pro.",
    accent: "#7c3aed",
  },
];

export function NewAccountForm() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState<PlanKey>("FREE");
  const [inviteMode, setInviteMode] = useState<"admin_password" | "email_invite">("email_invite");
  const [trialDays, setTrialDays] = useState<string>("14");

  const trialN = Math.min(365, Math.max(0, parseInt(trialDays, 10) || 0));
  const needsPaymentNotice =
    (plan === "PRO" || plan === "ENTERPRISE") && trialN === 0;

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const password = String(fd.get("password") ?? "").trim();
    const body = {
      name: String(fd.get("name")),
      slug: String(fd.get("slug")),
      email: String(fd.get("email")),
      plan,
      inviteMode,
      trialDays: trialN,
      ...(inviteMode === "admin_password" && password ? { password } : {}),
    };
    setBusy(true);
    const r = await fetchWithSessionSync(
      "/api/admin/professionals",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
      refresh,
    );
    const data = (await r.json()) as { error?: string; id?: string };
    setBusy(false);
    if (!r.ok) {
      setError(data.error ?? "No se pudo crear la cuenta.");
      return;
    }
    if (data.id) navigate(`/admin/professionals/${data.id}`);
  }

  return (
    <form onSubmit={(e) => void submit(e)} className={cx(cf.card, cf.cardPadding, css({ maxW: "42rem" }))}>
      {error ? <p className={css({ fontSize: "sm", color: "red.600", mb: "4" })}>{error}</p> : null}

      <p className={css({ fontSize: "sm", color: "fg.muted", mb: "6", lineHeight: "1.6" })}>
        Elegí el plan, los días de prueba (0 = sin prueba) y cómo accede la persona a su cuenta. Con Pro o Enterprise{" "}
        <strong>sin días de prueba</strong>, el sistema marca que debe <strong>coordinar el pago</strong> de la mensualidad.
      </p>

      <fieldset className={css({ border: "none", p: "0", m: "0", mb: "6" })}>
        <legend className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "3", display: "block" })}>
          Plan
        </legend>
        <div className={css({ display: "grid", gap: "3", gridTemplateColumns: { base: "1fr", md: "repeat(3, 1fr)" } })}>
          {planCards.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPlan(p.key)}
              className={css({
                textAlign: "left",
                p: "4",
                rounded: "xl",
                borderWidth: "2px",
                borderColor: plan === p.key ? p.accent : "#e2e8f0",
                bg: plan === p.key ? "rgba(79,70,229,0.06)" : "white",
                cursor: "pointer",
                transition: "border-color 0.15s",
              })}
            >
              <span className={css({ fontWeight: "bold", color: "#0f172a", display: "block", mb: "1" })}>{p.title}</span>
              <span className={css({ fontSize: "xs", color: "#64748b", lineHeight: "1.45" })}>{p.blurb}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className={css({ display: "flex", flexDir: "column", gap: "4", mb: "6" })}>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Nombre público</span>
          <input name="name" required minLength={2} className={cf.input} />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Enlace (slug)</span>
          <input name="slug" required pattern="[a-z0-9]+(-[a-z0-9]+)*" className={cf.input} placeholder="tu-estudio" />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Email del profesional</span>
          <input name="email" type="email" required autoComplete="email" className={cf.input} />
        </label>

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Días de prueba gratuita</span>
          <input
            type="number"
            min={0}
            max={365}
            value={trialDays}
            onChange={(e) => setTrialDays(e.target.value)}
            className={cf.input}
          />
          <span className={css({ fontSize: "xs", color: "#64748b" })}>
            Durante este período se aplica el plan elegido arriba y se fija fecha de vencimiento. Usá <strong>0</strong> si no
            corresponde prueba. Para <strong>extender o renovar</strong> días después, usá la cuenta en admin → “Renovar prueba”.
          </span>
        </label>
      </div>

      <fieldset className={css({ border: "none", p: "0", m: "0", mb: "6" })}>
        <legend className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "3", display: "block" })}>
          Acceso por primera vez
        </legend>
        <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          <label className={css({ display: "flex", gap: "2", alignItems: "flex-start", cursor: "pointer" })}>
            <input
              type="radio"
              name="inviteMode"
              checked={inviteMode === "email_invite"}
              onChange={() => setInviteMode("email_invite")}
            />
            <span>
              <strong>Enviar enlace por email</strong> para que defina su contraseña (recomendado para Pro / Enterprise).
              <span className={css({ display: "block", fontSize: "xs", color: "#64748b", mt: "1" })}>
                Recibe un correo con enlace seguro (48 h). Si el plan es de pago sin prueba, el mail aclara que debe coordinar la
                mensualidad.
              </span>
            </span>
          </label>
          <label className={css({ display: "flex", gap: "2", alignItems: "flex-start", cursor: "pointer" })}>
            <input
              type="radio"
              name="inviteMode"
              checked={inviteMode === "admin_password"}
              onChange={() => setInviteMode("admin_password")}
            />
            <span>
              <strong>Yo defino la contraseña inicial</strong>
              <span className={css({ display: "block", fontSize: "xs", color: "#64748b", mt: "1" })}>
                La persona entra con este email y la contraseña que cargues abajo.
              </span>
            </span>
          </label>
        </div>
      </fieldset>

      {inviteMode === "admin_password" ? (
        <label className={css({ display: "flex", flexDir: "column", gap: "1", mb: "6" })}>
          <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Contraseña inicial (mín. 8)</span>
          <input name="password" type="password" required minLength={8} autoComplete="new-password" className={cf.input} />
        </label>
      ) : null}

      {needsPaymentNotice ? (
        <div
          className={css({
            mb: "6",
            p: "4",
            rounded: "lg",
            bg: "#fffbeb",
            borderWidth: "1px",
            borderColor: "#fcd34d",
            fontSize: "sm",
            color: "#92400e",
            lineHeight: "1.5",
          })}
        >
          <strong>Pago de plan:</strong> al crear la cuenta sin días de prueba, el usuario verá en el panel que debe{" "}
          <strong>regularizar la mensualidad</strong> (contacto comercial / Suscripción) después de entrar.
        </div>
      ) : null}

      <div className={css({ display: "flex", gap: "3", flexWrap: "wrap", mt: "2" })}>
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Creando…" : "Crear cuenta"}
        </Button>
        <Link
          to="/admin/professionals"
          className={css({
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            px: "4",
            py: "2.5",
            rounded: "lg",
            fontSize: "sm",
            fontWeight: "semibold",
            bg: "bg.muted",
            color: "fg.DEFAULT",
            borderWidth: "1px",
            borderColor: "border.DEFAULT",
            _hover: { bg: "bg.card" },
          })}
        >
          Cancelar
        </Link>
      </div>
    </form>
  );
}
